import { createContext, useContext, useEffect, useState } from 'react';
import { themeConfig } from '../../config/theme';
import { usePreferences } from '../../hooks/api/usePreferences';
import { useAuth } from '../../hooks/useAuth';

type Theme = 'dark' | 'light' | 'system';

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: Theme;
  storageKey?: string;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

const initialState: ThemeProviderState = {
  theme: 'system',
  setTheme: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultTheme = 'system',
  storageKey = 'conciliator-theme',
}: ThemeProviderProps) {
  const { isAuthenticated } = useAuth();
  const { preferences, updatePreferences } = usePreferences();

  // Use public theme config for unauthenticated users
  const publicTheme = themeConfig.public.default;

  // Determine initial theme based on auth state
  const [theme, setTheme] = useState<Theme>(() => {
    if (!isAuthenticated) {
      return publicTheme;
    }
    return (localStorage.getItem(storageKey) as Theme) || defaultTheme;
  });

  // Sync theme with user preferences when authenticated
  useEffect(() => {
    if (isAuthenticated && preferences?.theme) {
      setTheme(preferences.theme);
    } else if (!isAuthenticated) {
      setTheme(publicTheme);
    }
  }, [isAuthenticated, preferences?.theme, publicTheme]);

  useEffect(() => {
    const root = window.document.documentElement;

    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';

      root.classList.add(systemTheme);
      return;
    }

    root.classList.add(theme);
  }, [theme]);

  const value = {
    theme,
    setTheme: (newTheme: Theme) => {
      localStorage.setItem(storageKey, newTheme);
      setTheme(newTheme);

      // Update user preferences if authenticated
      if (isAuthenticated && updatePreferences) {
        updatePreferences({ theme: newTheme });
      }
    },
  };

  return <ThemeProviderContext.Provider value={value}>{children}</ThemeProviderContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeProviderContext);

  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
}
